import { apiUtils } from '../hooks/useApi';
import { throwApiError } from '../utils';
import {
  API_ENDPOINTS,
  SabreSearchParams,
  GiataIdSearchParams,
  HotelSearchResponse,
  HotelMovedResponse,
  CountrySearchParams,
  DestinationSearchParams,
  CitySearchParams,
  CountrySearchResponse,
  DestinationSearchResponse,
  CitySearchResponse,
  BulkUploadResponse,
} from './apiEndpoints';

export interface HotelLinkUser {
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
}

export interface HotelLinkOrganization {
  name: string;
  metadata: {
    user_count: number;
  };
  users: HotelLinkUser[];
}

export interface HotelLinksResponse {
  data: {
    organizations: HotelLinkOrganization[];
    enterprises: HotelLinkOrganization[];
  };
}

export interface AcquireHotelsResponse {
  success: boolean;
  message: string;
  data: {
    summary: {
      totalFetched: number;
      selectedCount: number;
      toAcquireCount: number;
      alreadyExistsCount: number;
      createdInDb: number;
      failedCreations: number;
    };
    acquiredGiataIds: string[];
    mode: string;
  };
  _timezone: {
    requested: string;
    applied: boolean;
  };
}

export interface BenchmarkMetric {
  hotel_score: number;
  rank: number;
  totalHotels: number;
  average: number;
  topValue: number;
  topHotelName: string;
  valueGapToTop: number;
  ranksBehindTop: number;
}

export interface BenchmarkContext {
  type: string;
  totalHotels: number;
}

export interface BenchmarksData {
  score_alo: BenchmarkMetric;
  score_e: BenchmarkMetric;
  score_s: BenchmarkMetric;
  score_g: BenchmarkMetric;
  Biodiversity: BenchmarkMetric;
  'Climate Action': BenchmarkMetric;
  Energy: BenchmarkMetric;
  Waste: BenchmarkMetric;
  'Water Stewardship': BenchmarkMetric;
  'Community & Cultural Stewardship': BenchmarkMetric;
  'Human Rights, Justice & Wellbeing': BenchmarkMetric;
  'Responsible Sourcing': BenchmarkMetric;
  Accessibility: BenchmarkMetric;
  Auditability: BenchmarkMetric;
  'Training & Engagement': BenchmarkMetric;
  'Strategy & Management': BenchmarkMetric;
}

export interface OverallBenchmark {
  benchmarkContext: BenchmarkContext;
  benchmarks: BenchmarksData;
}

export interface BrandBenchmark {
  brandId: string;
  brandName: string;
  benchmarks: BenchmarksData;
  totalHotels: number;
}

export interface MarketBenchmark {
  market: string;
  benchmarks: BenchmarksData;
  totalHotels: number;
  country: string;
}

export interface BenchMarkData{
  hotelId: string;
    hotelName: string;
    subscriptionStatus: string;
    view: string;
    overall: OverallBenchmark;
    brand: BrandBenchmark[];
    market: MarketBenchmark;
}

export interface HotelBenchmarkReportResponse {
  success: boolean;
  data: BenchMarkData
}

class HotelsService {
  /**
   * Search hotel by Sabre GDS provider (hotel chain + property ID)
   * @param params - Hotel chain code and Sabre ID
   * @returns Hotel search response with GIATA data
   */
  async searchBySabre(params: SabreSearchParams): Promise<HotelSearchResponse> {
    try {
      const response = await apiUtils.get<HotelSearchResponse>(
        API_ENDPOINTS.hotels.searchBySabre,
        {
          hotel_chain: params.hotel_chain,
          sabre_id: params.sabre_id,
        }
      );

      return response.data as HotelSearchResponse;
    } catch (error: any) {
      throwApiError(error, 'Failed to search hotel by Sabre GDS');
      throw error;
    }
  }

  /**
   * Search hotel by GIATA ID only
   * @param params - GIATA ID
   * @returns Hotel search response with GIATA data or moved response
   */
  async searchByGiataId(
    params: GiataIdSearchParams
  ): Promise<HotelSearchResponse | HotelMovedResponse> {
    try {
      const response = await apiUtils.get(
        API_ENDPOINTS.hotels.searchByGiataId,
        {
          giataId: params.giataId,
        }
      );

      return response.data as HotelSearchResponse;
    } catch (error: any) {
      // Handle 301 Moved Permanently - hotel ID changed
      if (
        error.response?.status === 301 &&
        error.response?.data?.error === 'HOTEL_MOVED'
      ) {
        return error.response.data as HotelMovedResponse;
      }

      throwApiError(error, 'Failed to search hotel by GIATA ID');
      throw error;
    }
  }

  /**
   * Search countries by name
   * @param params - Country name search term
   * @returns Country search response
   */
  async searchCountries(
    params: CountrySearchParams
  ): Promise<CountrySearchResponse> {
    try {
      const response = await apiUtils.get(
        API_ENDPOINTS.hotels.searchCountries,
        { name: params.name }
      );

      return response.data as CountrySearchResponse;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        'Failed to search countries';
      throw new Error(errorMessage);
    }
  }

  /**
   * Search destinations by country and name
   * @param params - Country ID and destination name search term
   * @returns Destination search response
   */
  async searchDestinations(
    params: DestinationSearchParams
  ): Promise<DestinationSearchResponse> {
    try {
      const response = await apiUtils.get(
        API_ENDPOINTS.hotels.searchDestinations,
        {
          country_id: params.country_id,
          name: params.name,
        }
      );

      return response.data as DestinationSearchResponse;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        'Failed to search destinations';
      throw new Error(errorMessage);
    }
  }

  /**
   * Search cities by country, destination (optional), and name
   * @param params - Country ID, optional destination ID, and city name search term
   * @returns City search response
   */
  async searchCities(params: CitySearchParams): Promise<CitySearchResponse> {
    try {
      const queryParams: Record<string, any> = {
        country_id: params.country_id,
        name: params.name,
      };

      if (params.destination_id) {
        queryParams.destination_id = params.destination_id;
      }

      const response = await apiUtils.get(
        API_ENDPOINTS.hotels.searchCities,
        queryParams
      );

      return response.data as CitySearchResponse;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        'Failed to search cities';
      throw new Error(errorMessage);
    }
  }

  async searchMultipleHotels(params: {
    CountryId?: string;
    DestinationId?: string;
    CityId?: string;
    offset?: number;
  }): Promise<any> {
    try {
      const response = await apiUtils.get(
        API_ENDPOINTS.hotels.searchMultipleHotels,
        { ...params }
      );
      console.log('Multiple Hotels Search Response:', response.data);
      return response.data;
    } catch (error: any) {
      throwApiError(error, 'Failed to search multiple hotels');
      throw error;
    }
  }
  /**
   *
   * @param hotelData
   */
  async createHotel(hotelData: any): Promise<void> {
    try {
      await apiUtils.post(API_ENDPOINTS.hotels.createHotel, hotelData);
    } catch (error: any) {
      throwApiError(error, 'Failed to create hotel');
    }
  }

  /**
   * Bulk upload hotels via Excel file
   * @param file - Excel file containing hotel data
   * @returns Response from bulk upload
   */
  async bulkUploadHotels(file: File, enterpriseId?: string): Promise<BulkUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (enterpriseId) {
        formData.append('enterpriseId', enterpriseId);
      }

      const response = await apiUtils.post(
        API_ENDPOINTS.hotels.bulkCreate,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data as BulkUploadResponse;
    } catch (error: any) {
      const responseData = error?.response?.data;
      if (responseData?.error === 'INVALID_HEADERS') {
        const err = new Error(responseData.message || 'Invalid XLSX headers') as any;
        err.code = 'INVALID_HEADERS';
        err.details = responseData.details;
        throw err;
      }
      throwApiError(error, 'Failed to upload bulk hotels');
      throw error;
    }
  }

  async acquireHotels(giataIds: string[], loadedOffsets: number[], countryId : string, acquireAll : boolean, destinationId ?: string, cityId ? : string): Promise<AcquireHotelsResponse> {
    try {
      const response = await apiUtils.post(API_ENDPOINTS.hotels.acquire, {
        giataIds,
        country_id : countryId,
        destination_id : destinationId,
        city_id : cityId,
        offsets: loadedOffsets,
        acquireAll
      })

      return response.data as AcquireHotelsResponse;
    } catch (error) {
      throwApiError(error, 'Failed to acquire hotels');
      throw error;
    }
  }

  /**
   * Get hotel links (organizations and enterprises)
   * @param hotelId - Hotel ID
   * @returns Hotel links response with organizations and enterprises
   */
  async getHotelLinks(hotelId: string): Promise<HotelLinksResponse> {
    try {
      const response = await apiUtils.get(API_ENDPOINTS.hotels.links(hotelId));
      return response.data;
    } catch (error: any) {
      throwApiError(error, 'Failed to fetch hotel links');
      throw error;
    }
  }

  /**
   * Export hotels data - queues export job and sends CSV via email
   * @returns Export job response with status and delivery information
   */
  async exportHotels(): Promise<{
    success: boolean;
    message: string;
    data?: {
      jobId: string;
      status: string;
      estimatedTime: string;
      deliveryMethod: string;
    };
  }> {
    try {
      const response = await apiUtils.get(API_ENDPOINTS.hotels.exportHotels);
      return response.data;
    } catch (error: any) {
      throwApiError(error, 'Failed to export hotels');
      throw error;
    }
  }

  async syncImagesFromGiata(hotelId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiUtils.post(API_ENDPOINTS.travellerFacing.syncImagesFromGiata(hotelId));
      return response.data;
    } catch (error: any) {
      throwApiError(error, 'Failed to sync images from Giata');
      throw error;
    }
  }

  async hotelBenchmarkReport(hotelId: string): Promise<HotelBenchmarkReportResponse> {
    try {
      const response = await apiUtils.get<HotelBenchmarkReportResponse>(API_ENDPOINTS.hotels.benchmark(hotelId));
      return response.data;
    } catch (error: any) {
      throwApiError(error, 'Failed to benchmark hotel');
      throw error;
    }
  }
}

export const hotelsService = new HotelsService();
